'use client'

import * as React from 'react'
import { useForm, useStore } from '@tanstack/react-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod/v4'

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldError,
  FieldLabel,
} from '@ui/web/components'
import { FormattedPriceInput } from '@ui/web/lib'
import { formatCurrency } from '@helper/utils/format'

import { useSharedTRPC } from '../../context/trpc'

const requestWithdrawalSchema = z.object({
  amount: z.number().positive('Amount must be positive'),
})

export interface RequestWithdrawalDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentBalance: number
  currency: string
  onSuccess?: () => void
}

export function RequestWithdrawalDialog({
  open,
  onOpenChange,
  currentBalance,
  currency,
  onSuccess,
}: RequestWithdrawalDialogProps) {
  const trpc = useSharedTRPC()
  const queryClient = useQueryClient()

  const requestWithdrawal = useMutation(
    trpc.royalty.requestWithdrawal.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: trpc.royalty.getRunningBalance.queryKey() })
        onOpenChange(false)
        onSuccess?.()
      },
    }),
  )

  const form = useForm({
    defaultValues: {
      amount: 0,
    },
    validators: {
      onSubmit: requestWithdrawalSchema,
    },
    onSubmit: async ({ value, formApi }) => {
      if (value.amount > currentBalance) {
        formApi.setErrorMap({
          // @ts-expect-error - form-level error as string
          onSubmit: `Insufficient balance. Current balance: ${formatCurrency(currentBalance, {
            currency,
            maximumFractionDigits: 2,
          })}`,
        })
        return
      }
      try {
        await requestWithdrawal.mutateAsync({
          amount: value.amount,
          currency,
        })
        formApi.reset()
      } catch (err) {
        // @ts-expect-error - form-level error as string
        formApi.setErrorMap({ onSubmit: (err as { message: string }).message })
      }
    },
  })

  React.useEffect(() => {
    if (!open) {
      form.reset()
    }
  }, [open, form])

  const errorMap = useStore(form.store, (state) => state.errorMap)
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting)
  const amount = useStore(form.store, (state) => state.values.amount)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='srd:max-w-md'>
        <DialogHeader>
          <DialogTitle>Request withdrawal</DialogTitle>
          <DialogDescription>
            Enter the amount you want to withdraw. Available balance:{' '}
            {formatCurrency(currentBalance, { currency, maximumFractionDigits: 2 })}.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            void form.handleSubmit()
          }}
          className='srd:space-y-4'
        >
          <form.Field name='amount'>
            {(field) => {
              const isInvalid = field.state.meta.isTouched && field.state.meta.errors.length > 0
              return (
                <Field className='srd:gap-1' data-invalid={isInvalid}>
                  <FieldLabel htmlFor='withdrawal-amount'>Amount</FieldLabel>
                  <FormattedPriceInput
                    id='withdrawal-amount'
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder='0'
                    className='srd:w-full'
                    disabled={isSubmitting}
                    onBlur={field.handleBlur}
                    aria-invalid={isInvalid}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              )
            }}
          </form.Field>

          {errorMap?.onSubmit && typeof errorMap.onSubmit === 'string' ? (
            <FieldError errors={[{ message: errorMap.onSubmit }]} />
          ) : null}
          <DialogFooter>
            <Button type='button' variant='outline' onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type='submit' disabled={isSubmitting || amount <= 0} loading={isSubmitting}>
              {isSubmitting ? 'Submitting…' : 'Request withdrawal'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
